import type { BoardCard, BoardColumn } from './model.ts';

/**
 * Reordering inside a column by hand (US-026, Design reference § Manual
 * order). Pure: the board works out the neighbours a card lands between, as
 * displayed, and the rank it shows until the server's answer arrives.
 */

/** How far apart the server ranks cards (`BOARD_RANK_STEP` in db/contents.ts). */
export const BOARD_RANK_STEP = 1024;

/** The columns sorted on `boardRank`, so the only ones a card is reordered in. */
export const RANKABLE_COLUMNS: readonly BoardColumn[] = ['ideas', 'planning', 'to-post'];

export function isRankable(column: BoardColumn): boolean {
  return RANKABLE_COLUMNS.includes(column);
}

/** The cards a card goes between: either may be absent (the top, the bottom). */
export interface Neighbours {
  readonly above: BoardCard | null;
  readonly below: BoardCard | null;
}

/** The four steps the card menu offers. */
export type ReorderStep = 'up' | 'down' | 'top' | 'bottom';

export const REORDER_STEPS: readonly ReorderStep[] = ['up', 'down', 'top', 'bottom'];

/**
 * The neighbours of `cardId` dropped before the card at `index` of `list` (as
 * displayed, the dragged card still in it; `index = list.length` is the end),
 * or `null` when that leaves it where it is.
 */
export function insertionNeighbours(list: readonly BoardCard[], cardId: string, index: number): Neighbours | null {
  const from = list.findIndex((card) => card.cardId === cardId);
  if (from === -1 || index === from || index === from + 1) return null;
  const rest = list.filter((card) => card.cardId !== cardId);
  const at = index > from ? index - 1 : index;
  return { above: rest[at - 1] ?? null, below: rest[at] ?? null };
}

/** Where a step of the card menu puts `cardId` in `list`, or `null` when it is already there. */
export function stepNeighbours(list: readonly BoardCard[], cardId: string, step: ReorderStep): Neighbours | null {
  const from = list.findIndex((card) => card.cardId === cardId);
  if (from === -1) return null;
  switch (step) {
    case 'up':
      return from === 0 ? null : insertionNeighbours(list, cardId, from - 1);
    case 'down':
      return from === list.length - 1 ? null : insertionNeighbours(list, cardId, from + 2);
    case 'top':
      return from === 0 ? null : insertionNeighbours(list, cardId, 0);
    case 'bottom':
      return from === list.length - 1 ? null : insertionNeighbours(list, cardId, list.length);
  }
}

/** The card's rank under `above` and over `below`, as the server will work it out. */
export function rankBetween({ above, below }: Neighbours): number | null {
  const top = above === null ? null : rankOf(above);
  const bottom = below === null ? null : rankOf(below);
  if (top !== null && bottom !== null) return (top + bottom) / 2;
  if (top !== null) return top + BOARD_RANK_STEP;
  if (bottom !== null) return bottom - BOARD_RANK_STEP;
  return null;
}

export function rankOf(card: BoardCard): number {
  return card.type === 'idea' ? card.idea.boardRank : card.content.boardRank;
}

/** The card with another rank, so the column sorts it where it was put. */
export function withRank(card: BoardCard, rank: number): BoardCard {
  return card.type === 'idea'
    ? { ...card, idea: { ...card.idea, boardRank: rank } }
    : { ...card, content: { ...card.content, boardRank: rank } };
}
